import { useState } from "react";
import api, { apiErr, fmtMoney, fmtDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { StatusBadge } from "@/components/StatusBadge";
import { Check, X, PackagePlus, ReceiptText, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Field, NSelect, TInput } from "@/components/technique/fields";

const empty = { article_id: "", quantite: 1, date_souhaitee: "", priorite: "Normale", motif: "" };

function RequestForm({ fid, articles, onDone }) {
  const [f, setF] = useState(empty);
  const set = (k) => (v) => setF({ ...f, [k]: v });
  const art = articles.find((a) => a.id === f.article_id);
  const add = async () => {
    try { await api.post(`/technique/files/${fid}/material-requests`, { ...f, quantite: Number(f.quantite) }); toast.success("Demande transmise au Stock"); setF(empty); onDone(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  return (
    <div className="rounded-lg border bg-white p-4 grid sm:grid-cols-2 lg:grid-cols-6 gap-3 items-end">
      <Field label="Article (stock existant)" className="lg:col-span-2"><NSelect value={f.article_id} onChange={set("article_id")} placeholder="Choisir…" options={articles.map((a) => ({ value: a.id, label: `${a.designation} (${a.stock ?? 0} ${a.unite || "u"})` }))} testid="tech-mr-article" /></Field>
      <Field label={`Quantité${art ? ` (${art.unite || "u"})` : ""}`}><TInput type="number" min={0} value={f.quantite} onChange={set("quantite")} testid="tech-mr-qty" /></Field>
      <Field label="Date souhaitée"><TInput type="date" value={f.date_souhaitee} onChange={set("date_souhaitee")} testid="tech-mr-date" /></Field>
      <Field label="Priorité"><NSelect value={f.priorite} onChange={set("priorite")} options={["Basse", "Normale", "Haute", "Urgente"]} testid="tech-mr-prio" /></Field>
      <Button disabled={!f.article_id || !(Number(f.quantite) > 0)} onClick={add} data-testid="tech-mr-add" className="bg-brand-600 hover:bg-brand-700 text-white"><PackagePlus className="h-4 w-4 mr-1" />Demander</Button>
      <Field label="Motif" className="sm:col-span-2 lg:col-span-6"><TInput value={f.motif} onChange={set("motif")} placeholder="Ex. Coulage dalle R+1" testid="tech-mr-motif" /></Field>
    </div>
  );
}

function Decision({ m, warehouses, reload }) {
  const [mag, setMag] = useState("");
  const act = async (kind) => {
    try { await api.post(`/technique/material-requests/${m.id}/${kind}`, { magasin_id: mag }); toast.success(kind === "validate" ? "Sortie de stock validée" : "Demande rejetée"); reload(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  return (
    <div className="flex items-center gap-1.5">
      <div className="w-36"><NSelect value={mag} onChange={setMag} placeholder="Magasin…" options={warehouses.map((w) => ({ value: w.id, label: w.nom }))} testid={`tech-mr-mag-${m.id}`} /></div>
      <Button size="sm" disabled={!mag} onClick={() => act("validate")} className="bg-emerald-600 hover:bg-emerald-700 text-white h-8" data-testid={`tech-mr-validate-${m.id}`}><Check className="h-4 w-4" /></Button>
      <Button size="sm" variant="outline" onClick={() => act("reject")} className="h-8 text-rose-600" data-testid={`tech-mr-reject-${m.id}`}><X className="h-4 w-4" /></Button>
    </div>
  );
}

export default function TechMaterials({ data, articles, warehouses, reload }) {
  const toBill = data.material_requests.filter((m) => m.statut === "Validée" && !m.facturee);
  const bill = async () => {
    try { const r = await api.post(`/technique/files/${data.file.id}/invoice-materials`); toast.success(`Facture ${r.data.numero} créée`); reload(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const del = async (m) => { await api.delete(`/technique/material-requests/${m.id}`); reload(); };
  return (
    <div className="space-y-4">
      <RequestForm fid={data.file.id} articles={articles} onDone={reload} />
      <div className="flex items-center justify-between flex-wrap gap-2">
        <p className="text-sm text-slate-500">Une demande validée génère une <b>sortie de stock</b> sur le chantier ; les sorties sont ensuite <b>facturées au client</b>.</p>
        <Button variant="outline" disabled={!toBill.length} onClick={bill} data-testid="tech-mr-invoice"><ReceiptText className="h-4 w-4 mr-1" />Facturer les sorties ({toBill.length} · {fmtMoney(toBill.reduce((s, m) => s + m.quantite * (m.prix_unitaire || 0), 0))} HT)</Button>
      </div>
      <div className="rounded-lg border bg-white overflow-hidden">
        {data.material_requests.length === 0 ? <p className="px-4 py-3 text-sm text-slate-400">Aucune demande de matériaux.</p> : (
          <div className="divide-y">
            {data.material_requests.map((m) => (
              <div key={m.id} className="flex items-center gap-3 px-4 py-2.5 text-sm flex-wrap" data-testid={`tech-mr-row-${m.id}`}>
                <span className="font-mono text-xs text-slate-500 w-20">{m.numero}</span>
                <div className="flex-1 min-w-[160px]"><p className="font-medium">{m.article_name} — {m.quantite} {m.unite}</p>
                  <p className="text-xs text-slate-400">{m.priorite} · {m.date_souhaitee ? fmtDate(m.date_souhaitee) : "sans date"} · par {m.requested_by}{m.motif ? ` · ${m.motif}` : ""}</p>
                  {m.movement_numero && <p className="text-xs text-emerald-700">Sortie {m.movement_numero} ({m.magasin_name}){m.invoice_numero ? ` · Facture ${m.invoice_numero}` : " · à facturer"}</p>}</div>
                <StatusBadge value={m.statut} />
                {m.statut === "En attente" && <><Decision m={m} warehouses={warehouses} reload={reload} />
                  <Button size="icon" variant="ghost" onClick={() => del(m)} data-testid={`tech-mr-del-${m.id}`}><Trash2 className="h-4 w-4" /></Button></>}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
